<script setup lang="ts">
import type { GenericImage } from '.';
import type { CarouselApi } from '@/components/ui/carousel';

import {
	Carousel,
	CarouselContent,
	CarouselItem
} from '@/components/ui/carousel';
import { cn } from '~/utils';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import ZoomableControl from '~/components/ui/zoomable-image/ZoomableControl.vue';
import ZoomableImage from '~/components/ui/zoomable-image/ZoomableImage.vue';

import { THUMBNAIL_CAROUSEL_IMAGE_ATTRS } from './imageAttrs';
import ThumbnailActionsWrapper from './ThumbnailActionsWrapper.vue';
import ThumbnailNavigationWrapper from './ThumbnailNavigationWrapper.vue';

const props = withDefaults(
	defineProps<{
		images: GenericImage[];
		altCallback: (index: number) => string;
		compactMobileThumbnails?: boolean;
		showPagination?: boolean;
	}>(),
	{
		compactMobileThumbnails: false,
		showPagination: true
	}
);

const emblaMainApi = ref<CarouselApi>();
const emblaThumbnailApi = ref<CarouselApi>();
const emblaSelectorsApi = ref<CarouselApi>();
const emblaSelectorsZoomableApi = ref<CarouselApi>();
const selectedIndex = defineModel<number>('selectedIndex', {
	required: true,
	default: 0
});

const isZoomed = ref(false);
const selectedImage = ref<GenericImage | null>(null);
const zoomCoords = ref({ x: 0, y: 0 });

const handleZoomImage = (zoom: boolean) => {
	isZoomed.value = zoom;
};

const emits = defineEmits<{
	(event: 'update-zoom', zoom: boolean): void;
}>();

watch(
	() => isZoomed.value,
	() => {
		emits('update-zoom', isZoomed.value);
	}
);

watch(
	() => selectedIndex.value,
	() => {
		selectedImage.value = null;
		handleZoomImage(false);
		zoomCoords.value = { x: 0, y: 0 };
	}
);

function onSelect() {
	if (!emblaMainApi.value || !emblaThumbnailApi.value) return;
	selectedIndex.value = emblaMainApi.value.selectedScrollSnap();
	emblaThumbnailApi.value.scrollTo(emblaMainApi.value.selectedScrollSnap());
	emblaSelectorsApi.value?.scrollTo(emblaMainApi.value.selectedScrollSnap());
}

async function closeZoomAndThumbClick(index: number) {
	await closeZoom();
	onThumbClick(index);
}

async function navigateFromMainActions(index: number) {
	if (selectedImage.value) {
		await closeZoomAndThumbClick(index);
		return;
	}

	scrollTo(index);
}

async function handleThumbnailClick(index: number) {
	if (isZoomed.value) {
		await closeZoomAndThumbClick(index);
		return;
	}

	onThumbClick(index);
}

function onThumbClick(index: number) {
	if (!emblaMainApi.value || !emblaThumbnailApi.value) return;
	emblaMainApi.value.scrollTo(index);
}

async function scrollTo(index: number, jump: boolean = false) {
	if (!emblaMainApi.value) return;

	emblaMainApi.value.scrollTo(index, jump);
	emblaThumbnailApi.value?.scrollTo(index, jump);
	emblaSelectorsApi.value?.scrollTo(index, jump);
}

function setupCarouselApi(api: CarouselApi) {
	emblaMainApi.value = api;

	emblaMainApi.value?.on('select', onSelect);
	emblaMainApi.value?.on('reInit', onSelect);

	scrollTo(selectedIndex.value);
}

function setupThumbnailCarouselApi(api: CarouselApi) {
	emblaThumbnailApi.value = api;
}

function setupSelectorsCarouselApi(api: CarouselApi) {
	emblaSelectorsApi.value = api;
}

function setupSelectorsCarouselZoomableApi(api: CarouselApi) {
	if (!emblaMainApi.value) return;
	emblaSelectorsZoomableApi.value = api;

	emblaSelectorsZoomableApi?.value?.scrollTo(
		emblaMainApi.value.selectedScrollSnap()
	);
}

const isFirstItem = computed(() => selectedIndex.value === 0);
const isLastItem = computed(
	() => selectedIndex.value === props.images.length - 1
);

// Global keyboard navigation
async function handleGlobalKeydown(event: KeyboardEvent) {
	if (event.key === 'ArrowLeft') {
		if (!isFirstItem.value) {
			event.preventDefault();
			event.stopPropagation();
			if (isZoomed.value) {
				await closeZoom();
			}
			scrollTo(selectedIndex.value - 1);
		}
	} else if (event.key === 'ArrowRight') {
		if (!isLastItem.value) {
			event.preventDefault();
			event.stopPropagation();
			if (isZoomed.value) {
				await closeZoom();
			}
			scrollTo(selectedIndex.value + 1);
		}
	} else if (event.key === 'Escape' && isZoomed.value) {
		event.preventDefault();
		event.stopPropagation();
		await closeZoom();
	}
}

onMounted(() => {
	window.addEventListener('keydown', handleGlobalKeydown);
});

onUnmounted(() => {
	window.removeEventListener('keydown', handleGlobalKeydown);
});
function openZoom(image: GenericImage, coords: { x: number; y: number }) {
	selectedImage.value = image;
	isZoomed.value = true;
	zoomCoords.value = coords;
}

async function closeZoom() {
	selectedImage.value = null;
	isZoomed.value = false;
	zoomCoords.value = { x: 0, y: 0 };

	await until(() => emblaMainApi.value).not.toBeNull();
	scrollTo(selectedIndex.value, true);

	// Wait for the carousel to finish scrolling
	await new Promise(resolve => setTimeout(resolve, 25));
}
</script>
<template>
	<div class="relative h-full">
		<section
			v-if="selectedImage"
			class="relative flex h-full w-full flex-col items-center justify-center"
		>
			<ZoomableImage
				:image-props="{
					src: selectedImage.watermarkUrl || selectedImage.url || ''
				}"
				:zoom-position="zoomCoords"
				@close="closeZoom"
			/>

			<ThumbnailNavigationWrapper
				v-if="showPagination"
				class-name="mx-auto mt-5 flex h-5 items-center justify-center focus:outline-none focus:ring-0 md:w-[30%]"
				:images="images"
				:selected-index="selectedIndex"
				@init-api="setupSelectorsCarouselZoomableApi"
				@thumb-click="closeZoomAndThumbClick"
			/>
		</section>

		<Container
			v-else
			data-testid="modal-carousel-main-container"
			main-class="h-full relative"
			class="relative h-full px-0 md:pb-10 md:pt-6"
		>
			<Carousel
				class="relative mx-auto flex h-full flex-col outline-none focus:outline-none md:w-2/3"
				:is-zoomed="isZoomed"
				:disable-keyboard="true"
				:plugins="[WheelGesturesPlugin()]"
				@init-api="setupCarouselApi"
			>
				<CarouselContent class="size-full gap-4">
					<CarouselItem
						v-for="(image, index) in images"
						:key="`principal-thumbnail-carousel-item: ${index}`"
						class="flex h-full justify-center"
					>
						<ZoomableControl
							v-slot="{ canZoom }"
							@zoom-in="zoomPoint => openZoom(image, zoomPoint)"
						>
							<NuxtImg
								data-testid="modal-carousel-image"
								:src="image.watermarkUrl || image.url"
								:format="THUMBNAIL_CAROUSEL_IMAGE_ATTRS.format"
								:width="THUMBNAIL_CAROUSEL_IMAGE_ATTRS.width"
								:sizes="THUMBNAIL_CAROUSEL_IMAGE_ATTRS.sizes"
								:densities="THUMBNAIL_CAROUSEL_IMAGE_ATTRS.densities"
								:quality="THUMBNAIL_CAROUSEL_IMAGE_ATTRS.quality"
								class="max-h-full max-w-full object-contain object-center"
								:class="{ 'md:cursor-zoom-in': canZoom }"
								:placeholder="[50, 25]"
								:alt="altCallback(index)"
								:loading="index === selectedIndex ? 'eager' : 'lazy'"
							/>
						</ZoomableControl>
					</CarouselItem>
				</CarouselContent>
			</Carousel>

			<ThumbnailNavigationWrapper
				v-if="showPagination"
				class="mx-auto mt-5 flex h-5 items-center justify-center focus:outline-none focus:ring-0 md:w-[30%]"
				:images="images"
				:selected-index="selectedIndex"
				@init-api="setupSelectorsCarouselApi"
				@thumb-click="onThumbClick"
			/>
		</Container>

		<Container
			data-testid="modal-carousel-actions-layer"
			class="pointer-events-none absolute inset-x-0 top-0 h-full px-0 md:pb-10 md:pt-6"
			main-class="relative h-full"
		>
			<ThumbnailActionsWrapper
				:is-first-item="isFirstItem"
				:is-last-item="isLastItem"
				position="main"
				@prev="() => navigateFromMainActions(selectedIndex - 1)"
				@next="() => navigateFromMainActions(selectedIndex + 1)"
			/>
		</Container>

		<Container
			data-testid="modal-carousel-thumbnail-track"
			:class="
				cn(
					'border-t border-white-dark-3',
					compactMobileThumbnails
						? 'h-[76px] px-0 py-2 md:h-[116px] md:py-4'
						: 'h-[140px] py-6'
				)
			"
			main-class="relative"
		>
			<Carousel
				data-testid="modal-carousel-thumbnail-carousel"
				:class="
					cn(
						'mx-auto flex outline-none focus:outline-none',
						compactMobileThumbnails
							? 'w-full justify-start px-0 md:w-[90%] md:justify-center'
							: 'justify-center md:w-[90%]'
					)
				"
				:disable-keyboard="true"
				:opts="{
					dragFree: true,
					containScroll: 'trimSnaps',
					align: 'start'
				}"
				@init-api="setupThumbnailCarouselApi"
			>
				<CarouselContent
					data-testid="modal-carousel-thumbnail-content"
					:class="
						compactMobileThumbnails ? 'gap-2 pl-2 md:gap-4 md:pl-0' : 'gap-4'
					"
				>
					<CarouselItem
						v-for="(image, index) in images"
						:key="`secondary-thumbnail-carousel-item: ${index}`"
						data-testid="modal-carousel-thumbnail-item"
						:class="
							cn(
								'aspect-[3/2] cursor-pointer',
								compactMobileThumbnails
									? 'h-[60px] w-[90px] basis-[90px] md:h-[84px] md:w-[126px] md:basis-[126px]'
									: 'h-[88px] w-[132px] basis-[132px]',
								{
									'border-2 border-primary p-0.5':
										compactMobileThumbnails && index === selectedIndex,
									'border border-primary':
										!compactMobileThumbnails && index === selectedIndex
								}
							)
						"
						@click="handleThumbnailClick(index)"
					>
						<NuxtImg
							data-testid="modal-carousel-thumbnail-image"
							:src="image.watermarkUrl || image.url"
							format="webp"
							:width="compactMobileThumbnails ? 126 : 132"
							:height="compactMobileThumbnails ? 84 : 88"
							:sizes="compactMobileThumbnails ? '90px md:126px' : '132px'"
							densities="x1"
							:placeholder="[48, 32]"
							class="aspect-[3/2] size-full object-cover"
							:alt="altCallback(index)"
							loading="lazy"
						/>
					</CarouselItem>
				</CarouselContent>
			</Carousel>

			<ThumbnailActionsWrapper
				:is-first-item="isFirstItem"
				:is-last-item="isLastItem"
				position="thumbnail"
				@prev="() => scrollTo(selectedIndex - 1)"
				@next="() => scrollTo(selectedIndex + 1)"
			/>
		</Container>
	</div>
</template>
